import { useTranslation } from "@/language"
/**
 * StripeConnectCard
 * Displays Stripe Connect status with connect/manage/disconnect actions.
 */

import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import type { ConnectStatus } from "../services/earnings.service"

type StripeConnectCardProps = {
  connectStatus?: ConnectStatus
  isLoading: boolean
  isConnecting: boolean
  isDisconnecting: boolean
  isManaging: boolean
  onConnect: () => void
  onDisconnect: () => void
  onManage: () => void
}

export function StripeConnectCard({
  connectStatus,
  isLoading,
  isConnecting,
  isDisconnecting,
  isManaging,
  onConnect,
  onDisconnect,
  onManage,
}: StripeConnectCardProps) {
  const { t } = useTranslation("settings")
  if (isLoading) {
    return <StripeConnectCardSkeleton />
  }

  const isConnected = connectStatus?.connected ?? false
  const isChargeReady = connectStatus?.destinationChargeReady !== false

  return (
    <div className="flex flex-col md:flex-row lg:flex-col xl:flex-row justify-between items-start w-full gap-4">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2.5 mt-1.75 flex-wrap">
          <span className="text-[15px] font-semibold text-primary leading-6.25">
            Stripe Account
          </span>
          {isConnected && (
            <span className="px-1.5 py-0.5 rounded-[5px] bg-brand-olive text-[#F2F0EBE6] text-[13px] leading-5">
              Connected
            </span>
          )}
        </div>
        <p className="text-[13px] text-body mt-2.75">
          {isConnected ? t("stripeConnect.ready") : t("stripeConnect.setup")}
        </p>
        {isConnected && !isChargeReady && (
          <p className="text-[13px] text-amber-400 mt-2">
            Paid offerings are paused until your Stripe account is fully ready for charges and
            payouts.
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-center w-full md:w-fit gap-3 sm:justify-end shrink-0">
        {isConnected ? (
          <div className="flex flex-row gap-2.5">
            <Button
              variant="outline"
              size="sm"
              className="h-[50px] w-[150px] py-3.75 uppercase border-[#F2F0EB] text-sm font-semibold text-primary"
              onClick={onManage}
              isLoading={isManaging}
            >
              Manage
            </Button>
            <Button
              variant="primary-terracotta"
              size="sm"
              className="h-[50px] w-[150px] py-3.75 uppercase text-sm font-semibold"
              onClick={onDisconnect}
              isLoading={isDisconnecting}
            >
              Disconnect
            </Button>
          </div>
        ) : (
          <Button
            variant="primary"
            size="sm"
            className="h-[50px] w-full md:w-[220px] px-6 uppercase"
            onClick={onConnect}
            isLoading={isConnecting}
          >
            Connect with Stripe
          </Button>
        )}
      </div>
    </div>
  )
}

function StripeConnectCardSkeleton() {
  return (
    <div className="flex flex-col sm:flex-row justify-between w-full gap-4">
      <div className="min-w-0 space-y-2.75 flex-1">
        <div className="flex items-center gap-2.5 mt-1.75">
          <Skeleton className="h-[22px] w-[120px]" />
          <Skeleton className="h-[20px] w-[80px] rounded-[5px]" />
        </div>
        <Skeleton className="h-[16px] w-full max-w-[350px]" />
      </div>
      <div className="flex items-center gap-2.5 sm:justify-end shrink-0">
        <Skeleton className="h-[50px] w-[120px]" />
        <Skeleton className="h-[50px] w-[110px]" />
      </div>
    </div>
  )
}
